import { useEffect, FC } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQueryClient, useQuery } from "@tanstack/react-query";
import {
  Button,
  Combobox,
  Label,
  Separator,
  SheetHeader,
  SheetDescription,
  SheetTitle,
  TagsInput,
  TagsInputClear,
  TagsInputInput,
  TagsInputItem,
  TagsInputList,
  Textarea,
  toast,
  Typography,
} from "@altimateai/lego";
import { IconArrowLeft, IconRefresh } from "@tabler/icons-react";
import { getPromptTags } from "@/helpers/apis";
import { createPrompt, updatePrompt } from "@/helpers/apis";
import {
  CreateOrUpdatePromptRequest,
  CreateOrUpdatePromptRequestSchema,
  PromptResponse,
} from "./types";
import { LoadingButton, Stack } from "@altimateai/lego";
import useUserGroups from "@/pages/settings/components/userGroups/useUserGroups";
import { isAdmin, isOwner } from "@/services/auth";
import { useTenantInfo } from "@/helpers/hooks/useTenantInfo";
import { StudioTracking } from "@/lib/tracking/studio";

interface PromptFormProps {
  prompt?: PromptResponse;
  prefilledPromptText?: string;
  onClose: () => void;
}

const PromptForm: FC<PromptFormProps> = ({ prompt, prefilledPromptText, onClose }) => {
  const { rbacEnabled } = useTenantInfo();
  const queryClient = useQueryClient();

  // Derive isEditing from prompt prop to ensure correct schema is used from first render
  const isEditing = !!prompt;

  // Fetch teams using the hook
  const { groups: teams } = useUserGroups();

  // Fetch prompt tags
  const { data: tagsData } = useQuery({
    queryKey: ["promptTags"],
    queryFn: getPromptTags,
  });

  const teamOptions = teams.map(team => ({
    label: team.name,
    value: team.id.toString(),
  }));

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(CreateOrUpdatePromptRequestSchema),
    defaultValues: {
      title: "",
      description: "",
      prompt_text: prefilledPromptText || "",
      tags: [],
      teams: [],
    },
  });

  const watchedTags = watch("tags");
  const watchedTeams = watch("teams");

  // Auto-generate title from first line of prompt if empty
  useEffect(() => {
    const promptText = watch("prompt_text");
    if (promptText && !watch("title")) {
      const firstLine = promptText.split("\n")[0].trim();
      if (firstLine.length > 0) {
        setValue("title", firstLine);
      }
    }
  }, [watch, setValue]);

  // Reset form when editing prompt changes or prefilled text is provided
  useEffect(() => {
    if (prompt) {
      reset({
        title: prompt.title,
        description: prompt.description ?? "",
        prompt_text: prompt.prompt_text,
        tags: prompt.tags,
        teams: prompt.teams ?? [],
      });
    } else if (prefilledPromptText) {
      reset({
        title: "",
        description: "",
        prompt_text: prefilledPromptText,
        tags: [],
        teams: [],
      });
    }
  }, [prompt, prefilledPromptText, reset]);

  const createMutation = useMutation({
    mutationFn: (data: CreateOrUpdatePromptRequest) =>
      createPrompt({
        title: data.title,
        description: data.description,
        prompt_text: data.prompt_text,
        tags: data.tags,
        teams: data.teams,
      }),
    onSuccess: (response) => {
      if (response?.id) StudioTracking.promptSaved(response.id);
      queryClient.invalidateQueries({ queryKey: ["prompts"] });
      toast({
        title: "Prompt created successfully",
      });
      onClose();
    },
    onError: error => {
      toast({
        title: "Failed to create prompt",
        description: error instanceof Error ? error.message : String(error),
        variant: "destructive",
      });
    },
  });

  const updateMutation = useMutation({
    mutationFn: (data: CreateOrUpdatePromptRequest) =>
      updatePrompt(prompt!.id, {
        title: data.title,
        description: data.description,
        prompt_text: data.prompt_text,
        tags: data.tags,
        teams: data.teams,
      }),
    onSuccess: () => {
      StudioTracking.promptSaved(prompt!.id);
      queryClient.invalidateQueries({ queryKey: ["prompts"] });
      toast({
        title: "Prompt updated successfully",
      });
      onClose();
    },
    onError: error => {
      toast({
        title: "Failed to update prompt",
        description: error instanceof Error ? error.message : String(error),
        variant: "destructive",
      });
    },
  });

  const onSubmit = (data: CreateOrUpdatePromptRequest) => {
    if (isEditing) {
      updateMutation.mutate(data);
    } else {
      createMutation.mutate(data);
    }
  };

  const isLoading = createMutation.isPending || updateMutation.isPending;

  return (
    <>
      <SheetHeader className="al-px-1 al-pt-1">
        <Stack align={"center"}>
          <Button variant={"ghost"} onClick={onClose} title="View prompts">
            <IconArrowLeft size={20} />
          </Button>
          <SheetTitle className="al-text-xl al-font-semibold">
            {isEditing ? "Update prompt" : "Create a new Saved Prompt"}
            <SheetDescription>
              <Typography size={"sm"} variant={"caption"} weight={"regular"}>
                Save this prompt and reuse it quickly as needed
              </Typography>
            </SheetDescription>
          </SheetTitle>
        </Stack>
      </SheetHeader>
      <Separator className={"al-w-full al-mt-2 al-mb-4"} />
      <div className="al-px-1 al-pb-1">
        <form onSubmit={handleSubmit(onSubmit)} className="al-space-y-6">
          {/* Prompt Text */}
          <div>
            <Label htmlFor="prompt_text" className="al-text-gray-900 al-font-medium">
              Prompt
            </Label>
            <Textarea
              id="prompt_text"
              {...register("prompt_text")}
              placeholder="Analyze the stg_orders and fact_sales models, then generate a clear description explaining how raw order data flows into the final sales model, including key transformations and joins."
              className="al-min-h-32 al-mt-2"
            />
            {errors.prompt_text && (
              <p className="al-text-red-500 al-text-sm al-mt-1">{errors.prompt_text.message}</p>
            )}
          </div>

          {/* Horizontal separator */}
          <Separator className={"al-w-full"} />

          {/* Two column layout for tags and teams */}
          <div className="al-grid al-grid-cols-2 al-gap-6">
            {/* Left column - Add Category */}
            <div>
              <Label className="al-text-gray-900 al-font-medium al-mb-2 al-block">Add Tags</Label>
              <TagsInput
                suggestions={tagsData?.tags ?? []}
                onSuggestionSelect={(tag: string) => {
                  setValue("tags", [...(watchedTags ?? []), tag]);
                }}
                value={watchedTags}
                onValueChange={(tags: string[]) => {
                  setValue("tags", tags);
                }}
                addOnPaste
                delimiter=","
                className="al-w-full"
                disabled={isLoading}
              >
                <TagsInputList className={"!al-py-1 !al-px-2"}>
                  {watchedTags?.map(tag => (
                    <TagsInputItem key={tag} value={tag}>
                      {tag}
                    </TagsInputItem>
                  ))}
                  <TagsInputInput placeholder={"Add tags..."} disabled={isLoading} />
                </TagsInputList>
                <TagsInputClear asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    className="al-w-full"
                    disabled={isLoading || watchedTags?.length === 0}
                  >
                    <IconRefresh className="al-h-4 al-w-4" />
                    Clear All
                  </Button>
                </TagsInputClear>
              </TagsInput>
            </div>

            {/* Vertical separator */}
            {rbacEnabled && (isAdmin() || isOwner()) && (
              <div className="al-border-l al-border-gray-200 al-pl-6">
                {/* Right column - Associate with team */}
                <div>
                  <Label className="al-text-gray-900 al-font-medium al-mb-2 al-block">
                    Associate this prompt with a team
                  </Label>
                  <Combobox
                    placeholder="Select teams..."
                    value={watchedTeams}
                    options={teamOptions}
                    onChange={teams => setValue("teams", teams)}
                    multiSelect
                    buttonProps={{ variant: "outline", className: "al-w-full" }}
                  />
                </div>
              </div>
            )}
          </div>

          {/* Actions */}
          <Stack justify={"end"} className="al-w-full">
            <Button type="button" variant="outline" onClick={() => onClose()}>
              Cancel
            </Button>
            <LoadingButton loading={isLoading} type="submit" disabled={isLoading}>
              {isEditing ? "Save Changes" : "Save Prompt"}
            </LoadingButton>
          </Stack>
        </form>
      </div>
    </>
  );
};

export default PromptForm;
